import React, { useEffect, useState } from 'react';
import { adminApi } from '../../services/api';
import { toast } from 'sonner';
import {
  TrendingUp, Euro, ShoppingBag, Users, Zap, AlertTriangle,
  Loader2, Calendar, Award, ArrowUp, ArrowDown, Package,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  LineChart, Line,
} from 'recharts';


function KPI({ icon: Icon, label, value, sub, color = '#D44E20', testId, compare }) {
  const Trend = compare?.direction === 'up' ? ArrowUp : compare?.direction === 'down' ? ArrowDown : null;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}18` }}>
          <Icon size={17} style={{ color }} />
        </div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
      </div>
      <p className="text-3xl font-bold text-[#1A1A1A] tabular-nums">{value}</p>
      {(sub || compare) && (
        <div className="flex items-center gap-1.5 mt-1">
          {sub && <p className="text-xs text-slate-500">{sub}</p>}
          {compare && Trend && (
            <span className={`text-[11px] font-semibold flex items-center gap-0.5 ${compare.direction === 'up' ? 'text-emerald-600' : 'text-red-600'}`}>
              <Trend size={11} /> {compare.text}
            </span>
          )}
        </div>
      )}
    </div>
  );
}


function AlertBanner({ alert }) {
  const style = alert.severity === 'critical'
    ? 'bg-red-50 border-red-200 text-red-900'
    : alert.severity === 'warning'
      ? 'bg-amber-50 border-amber-200 text-amber-900'
      : 'bg-blue-50 border-blue-200 text-blue-900';
  const Icon = alert.severity === 'info' ? Zap : AlertTriangle;
  return (
    <div className={`border rounded-xl p-3 flex items-start gap-2 ${style}`} data-testid={`alert-${alert.type}`}>
      <Icon size={16} className="flex-shrink-0 mt-0.5" />
      <p className="text-xs font-medium leading-relaxed">{alert.message}</p>
    </div>
  );
}


export default function AdminSalesAnalytics() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [includeTickets, setIncludeTickets] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    adminApi.get(`/admin/sales-analytics?days=${days}&include_tickets=${includeTickets}`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Errore caricamento analytics'))
      .finally(() => setLoading(false));
  }, [days, includeTickets]);

  if (loading || !data) {
    return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-[#D44E20]" size={28} /></div>;
  }

  const k = data.kpis;
  const todayCompare = k.yesterday_revenue > 0
    ? {
        direction: k.today_revenue >= k.yesterday_revenue ? 'up' : 'down',
        text: `${Math.abs(Math.round(((k.today_revenue - k.yesterday_revenue) / k.yesterday_revenue) * 100))}% vs ieri`,
      }
    : null;

  const dailyChart = data.daily.map(d => ({
    ...d,
    day: new Date(d.date + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }),
  }));

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="sales-analytics-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#1A1A1A] mb-1">Analisi dello shop</h1>
          <p className="text-sm text-slate-500">Andamento ordini shop pagati, top prodotti, clienti e alert.</p>
        </div>
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl p-1">
          {[7, 30, 90].map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`days-filter-${d}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                days === d ? 'bg-[#D44E20] text-white' : 'text-slate-600 hover:text-[#D44E20]'
              }`}
            >
              {d} giorni
            </button>
          ))}
        </div>
      </div>

      {/* Alerts */}
      {data.alerts.length > 0 && (
        <div className="space-y-2 mb-6">
          {data.alerts.map((a, i) => <AlertBanner key={i} alert={a} />)}
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KPI
          icon={Euro}
          label="Oggi"
          value={`${k.today_revenue.toFixed(2)}€`}
          sub={`${k.today_orders} ordini`}
          testId="kpi-today"
          color="#D44E20"
          compare={todayCompare}
        />
        <KPI
          icon={TrendingUp}
          label="Ultimi 7 giorni"
          value={`${k.week_revenue.toFixed(2)}€`}
          testId="kpi-week"
          color="#10B981"
        />
        <KPI
          icon={ShoppingBag}
          label={`Totale ${data.period_days}g`}
          value={`${k.total_revenue.toFixed(2)}€`}
          sub={`${k.total_orders} ordini · ${k.total_items} articoli`}
          testId="kpi-total"
          color="#3B82F6"
        />
        <KPI
          icon={Calendar}
          label="Scontrino medio"
          value={`${k.avg_order_value.toFixed(2)}€`}
          testId="kpi-aov"
          color="#8B5CF6"
        />
      </div>

      {/* Loyalty attribution KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <KPI
          icon={Award}
          label="Punti assegnati al CRM"
          value={k.points_awarded.toLocaleString('it-IT')}
          sub="Collegati ai clienti che hanno acquistato"
          testId="kpi-points"
          color="#F59E0B"
        />
        <KPI
          icon={Users}
          label="Clienti attivi (CRM)"
          value={k.customers_reached}
          sub="Hanno ordinato nel periodo"
          testId="kpi-customers"
          color="#EC4899"
        />
        <KPI
          icon={Zap}
          label="Ultimo ordine"
          value={k.hours_since_last_order === null ? '—' : `${k.hours_since_last_order}h fa`}
          testId="kpi-last-order"
          color={k.hours_since_last_order && k.hours_since_last_order > 24 ? '#EF4444' : '#10B981'}
        />
      </div>

      {/* Daily revenue chart */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6" data-testid="daily-chart">
        <h3 className="text-base font-bold text-[#1A1A1A] mb-4">Fatturato giornaliero</h3>
        {data.daily.length === 0 || data.kpis.total_orders === 0 ? (
          <div className="py-12 text-center text-sm text-slate-500">Nessun dato da mostrare</div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={dailyChart} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#64748B' }} />
              <YAxis tick={{ fontSize: 11, fill: '#64748B' }} />
              <Tooltip
                cursor={{ fill: 'rgba(212, 78, 32, 0.05)' }}
                contentStyle={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 12, fontSize: 12 }}
                formatter={(value, name) => {
                  if (name === 'revenue') return [`${value.toFixed(2)}€`, 'Fatturato'];
                  if (name === 'orders') return [value, 'Ordini'];
                  return [value, name];
                }}
              />
              <Bar dataKey="revenue" fill="#D44E20" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Daily orders trend */}
      {data.daily.length > 0 && k.total_orders > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6" data-testid="orders-trend-chart">
          <h3 className="text-base font-bold text-[#1A1A1A] mb-4">Numero ordini / giorno</h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={dailyChart} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#64748B' }} />
              <YAxis tick={{ fontSize: 11, fill: '#64748B' }} />
              <Tooltip contentStyle={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 12, fontSize: 12 }} />
              <Line type="monotone" dataKey="orders" stroke="#10B981" strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top products */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="top-products">
          <div className="flex items-center gap-2 mb-4">
            <Package size={16} className="text-[#D44E20]" />
            <h3 className="text-base font-bold text-[#1A1A1A]">Top prodotti</h3>
          </div>
          {data.top_products.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center">Nessun prodotto venduto ancora</p>
          ) : (
            <div className="space-y-1.5">
              {data.top_products.map((p, i) => {
                const maxRev = data.top_products[0].revenue || 1;
                const pct = (p.revenue / maxRev) * 100;
                return (
                  <div key={p.item_id + i} className="p-2.5 rounded-xl hover:bg-slate-50 transition-colors" data-testid={`top-product-${i}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold text-[#1A1A1A] truncate">{i + 1}. {p.name}</span>
                      <span className="text-sm font-bold text-[#D44E20] tabular-nums">{p.revenue.toFixed(2)}€</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#D44E20] to-[#F59E0B] rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-[11px] text-slate-500 tabular-nums w-16 text-right">{p.qty} venduti</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top customers */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="top-customers">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Users size={16} className="text-[#D44E20]" />
              <h3 className="text-base font-bold text-[#1A1A1A]">Top clienti</h3>
            </div>
            <div className="flex gap-1 bg-slate-100 p-0.5 rounded-lg" data-testid="top-customers-source-switch">
              <button
                onClick={() => setIncludeTickets(false)}
                data-testid="top-customers-source-shop"
                className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition-all ${!includeTickets ? 'bg-white text-[#D44E20] shadow-sm' : 'text-[#64748B]'}`}
              >
                Solo shop
              </button>
              <button
                onClick={() => setIncludeTickets(true)}
                data-testid="top-customers-source-all"
                className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition-all ${includeTickets ? 'bg-white text-[#D44E20] shadow-sm' : 'text-[#64748B]'}`}
              >
                Shop + biglietti
              </button>
            </div>
          </div>
          {data.top_customers.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center">Nessun cliente ha ancora ordinato</p>
          ) : (
            <div className="space-y-2">
              {data.top_customers.map((c, i) => (
                <a
                  key={c.customer_id}
                  href={`/admin/customers/${c.customer_id}`}
                  className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 transition-colors block"
                  data-testid={`top-customer-${i}`}
                >
                  <div className="w-9 h-9 rounded-full bg-[#FDECE7] flex items-center justify-center flex-shrink-0">
                    <span className="text-[#D44E20] font-bold text-sm">{(c.name || '?').charAt(0).toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#1A1A1A] truncate">{c.name}</p>
                    <p className="text-[11px] text-slate-500">{c.orders} ordini · {c.points_earned} punti</p>
                  </div>
                  <p className="text-sm font-bold text-[#D44E20] tabular-nums">{c.total_spent.toFixed(2)}€</p>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
